import React, { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, ShieldCheck, Eye, EyeOff, KeyRound } from "lucide-react";

/**
 * Iter-117 · Password change tab (Settings → Password).
 * Uses the existing POST /auth/change-password endpoint. After success the
 * server invalidates the session (security), so we auto re-login with the new
 * password so the user isn't kicked to /login.
 */
export default function PasswordTab() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNw, setShowNw] = useState(false);
  const [busy, setBusy] = useState(false);

  const rules = [
    { ok: nw.length >= 8, txt: "At least 8 characters" },
    { ok: /[A-Z]/.test(nw), txt: "One uppercase letter (A-Z)" },
    { ok: /[a-z]/.test(nw), txt: "One lowercase letter (a-z)" },
    { ok: /[0-9]/.test(nw), txt: "One digit (0-9)" },
    { ok: /[^A-Za-z0-9]/.test(nw), txt: "One symbol (!@#$…)" },
    { ok: nw === nw2 && !!nw, txt: "Passwords match" },
    { ok: !!cur && nw !== cur, txt: "New password differs from current" },
  ];
  const allOk = rules.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) return;
    setBusy(true);
    try {
      // 1. Ask the server to rotate the password
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      // 2. Immediately relogin with the new password so the invalidated cookie
      //    gets replaced with a fresh one and the user stays on this page.
      const me = await api.get("/auth/me").catch(() => null);
      const email = me?.data?.user?.email;
      if (email) {
        try {
          await api.post("/auth/login", { email, password: nw });
          toast.success("Password changed — you're signed in with the new password.");
        } catch {
          toast.success("Password changed. Sign in again with the new password.");
          setTimeout(() => { window.location.href = "/login"; }, 1500);
        }
      } else {
        toast.success("Password changed. Sign in again.");
        setTimeout(() => { window.location.href = "/login"; }, 1500);
      }
      setCur(""); setNw(""); setNw2("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "Current password is incorrect") {
        toast.error("Your current password is wrong. Try again or use 'Forgot password?' on the login page.");
      } else if (typeof detail === "string" && detail.toLowerCase().includes("history")) {
        toast.error("You can't reuse a password you've used recently. Pick a different one.");
      } else {
        toast.error(detail || err?.message || "Password change failed");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-xl space-y-4" data-testid="password-tab">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-sm bg-accent/10 flex items-center justify-center">
          <KeyRound className="w-4 h-4 text-accent" />
        </div>
        <div>
          <div className="font-heading font-semibold text-base leading-none">Change password</div>
          <div className="text-[11px] text-muted-foreground mt-1">Rotate your password anytime. Your login session refreshes automatically.</div>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3 border border-border rounded-sm p-4 bg-card">
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Current password</Label>
          <div className="relative mt-1.5">
            <Input type={showCur ? "text" : "password"} value={cur} onChange={(e) => setCur(e.target.value)}
              required autoComplete="current-password" className="rounded-sm h-11 pr-9"
              data-testid="password-current" />
            <button type="button" onClick={() => setShowCur((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="password-current-toggle">
              {showCur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">New password</Label>
          <div className="relative mt-1.5">
            <Input type={showNw ? "text" : "password"} value={nw} onChange={(e) => setNw(e.target.value)}
              required autoComplete="new-password" minLength={8} className="rounded-sm h-11 pr-9"
              data-testid="password-new" />
            <button type="button" onClick={() => setShowNw((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="password-new-toggle">
              {showNw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Confirm new password</Label>
          <Input type={showNw ? "text" : "password"} value={nw2} onChange={(e) => setNw2(e.target.value)}
            required autoComplete="new-password" minLength={8} className="rounded-sm h-11 mt-1.5"
            data-testid="password-confirm" />
        </div>

        <div className="rounded-sm border border-border p-3 bg-muted/30 text-[11px] space-y-1"
              data-testid="password-rules">
          {rules.map((r, i) => (
            <div key={i} className={`flex items-center gap-1.5 ${r.ok ? "text-emerald-600" : "text-muted-foreground"}`}>
              <ShieldCheck className={`w-3 h-3 ${r.ok ? "text-emerald-600" : "text-muted-foreground/50"}`} />
              <span>{r.txt}</span>
            </div>
          ))}
        </div>

        <Button type="submit" disabled={!allOk || busy}
          className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white h-11 disabled:opacity-50"
          data-testid="password-submit">
          <Lock className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Change password"}
        </Button>
      </form>

      <div className="text-[11px] text-muted-foreground leading-relaxed">
        <b>Tip:</b> Use a password manager. Zenith 360 stores only the bcrypt hash — the plaintext never touches disk. If you forget your password, use "Forgot password?" on the sign-in page to receive a one-time reset link.
      </div>
    </div>
  );
}
